'use client';

import { intervalToDuration } from 'date-fns';
import { useEffect, useState } from 'react';
import { interpolateStringWithData } from 'utils';
import { isFree } from '../../../utils/price';
import { c } from '../../../utils/tailwind-merge';
import { formatCountdown } from '../../../utils/time';
import { Badge } from '../../atoms/Badge';
import { ButtonV2 } from '../../atoms/Button/Button.v2';
import { LinkV2 } from '../../atoms/Link/Link.v2';
import { Price } from '../../atoms/Price/Price';
import { QuantitySelector } from '../../molecules/QuantitySelector';
import { RichText } from '../../molecules/RichText/RichText';
import {
  ProductPricingProps,
  ProductPricingPropsActions,
} from './ProductPricing.types';

type ProductPricingPrimaryCta =
  'add-to-assets' | 'buy-now' | 'request-seat' | 'open-in-unity';

type ProductPricingHelpText =
  'none' | 'eula' | 'refund-policy' | 'org-owns-unassigned-seats';

type ProductPricingNotice =
  'none' | 'another-org-owns' | 'already-own-another-org';

type ProductPricingView = {
  showQuantitySelector: boolean;
  notice: ProductPricingNotice;
  primaryCta: ProductPricingPrimaryCta;
  helpText: ProductPricingHelpText;
  showSecondaryCta: boolean;
  showSecondaryHelpText: boolean;
};

// Single source of truth for the pricing layout. Maps the (pricing,
// entitlement, userType, seat, flag) matrix to an explicit view model so the
// JSX stays declarative. Missing userType is treated as 'user' and missing
// isSeatAssigned as assigned (the safe Open-in-Unity default).
const resolveProductPricingView = ({
  entitlement,
  price,
  userType,
  isSeatAssigned,
  isManageSeatEnabled,
  hasSeatData,
}: Pick<
  ProductPricingProps,
  | 'entitlement'
  | 'price'
  | 'userType'
  | 'isSeatAssigned'
  | 'isManageSeatEnabled'
  | 'hasSeatData'
>): ProductPricingView => {
  const isPaid = Boolean(price.originalPrice);
  const base: ProductPricingView = {
    showQuantitySelector: false,
    notice: 'none',
    primaryCta: 'open-in-unity',
    helpText: 'none',
    showSecondaryCta: false,
    showSecondaryHelpText: false,
  };

  if (!isPaid) {
    if (entitlement === 'not-entitled') {
      return { ...base, primaryCta: 'add-to-assets', helpText: 'eula' };
    }
    if (entitlement === 'entitled-another-org') {
      return {
        ...base,
        notice: 'another-org-owns',
        primaryCta: 'add-to-assets',
        helpText: 'eula',
      };
    }
    return base;
  }

  if (entitlement === 'preview') {
    return base;
  }

  if (entitlement === 'not-entitled') {
    return {
      ...base,
      showQuantitySelector: true,
      primaryCta: 'buy-now',
      helpText: 'refund-policy',
    };
  }

  // Paid owned asset with the flag off keeps today's Open-in-Unity behavior.
  if (!isManageSeatEnabled) {
    return base;
  }

  const seatAssigned = isSeatAssigned ?? true;
  const hasSeats = hasSeatData ?? false;
  const type = userType ?? 'user';

  if (entitlement === 'entitled-current-org') {
    if (!seatAssigned) {
      if (type === 'admin') {
        return {
          ...base,
          showQuantitySelector: true,
          primaryCta: 'buy-now',
          helpText: 'refund-policy',
          showSecondaryCta: true,
          showSecondaryHelpText: hasSeats,
        };
      }
      return {
        ...base,
        primaryCta: 'request-seat',
        helpText: hasSeats ? 'org-owns-unassigned-seats' : 'none',
      };
    }
    if (type === 'admin') {
      return { ...base, showSecondaryCta: true };
    }
    return base;
  }

  // entitled-another-org (userType-agnostic).
  return {
    ...base,
    showQuantitySelector: true,
    notice: seatAssigned ? 'already-own-another-org' : 'another-org-owns',
    primaryCta: 'buy-now',
    helpText: 'refund-policy',
  };
};

const primaryCtaLabel = (
  primaryCta: ProductPricingPrimaryCta,
  microcopy: ProductPricingProps['microcopy']
): string => {
  switch (primaryCta) {
    case 'add-to-assets':
      return microcopy.addToMyAssetSinglePackage;
    case 'buy-now':
      return microcopy.buyNow;
    case 'request-seat':
      return microcopy.requestSeat;
    case 'open-in-unity':
      return microcopy.openInUnity;
  }
};

const primaryCtaDataTest: Record<ProductPricingPrimaryCta, string> = {
  'add-to-assets': 'add-to-my-assets-button',
  'buy-now': 'buy-now-button',
  'request-seat': 'request-seat-button',
  'open-in-unity': 'open-in-unity-button',
};

const renderCtaButton = ({
  label,
  primaryCta,
  isLoading,
  iconRight,
  onClick,
}: {
  label: string;
  primaryCta: ProductPricingPrimaryCta;
  isLoading?: boolean;
  iconRight?: string;
  onClick: () => void;
}) => {
  return (
    <ButtonV2
      label={label}
      variant="primary"
      size="large"
      shape="square"
      onClick={onClick}
      className={c('min-w-0 flex-1', iconRight && !isLoading && 'gap-x-2')}
      disabled={isLoading}
      aria-busy={isLoading}
      iconLeft={isLoading ? 'SpinnerGap' : undefined}
      iconRight={isLoading ? undefined : iconRight}
      iconWeight="bold"
      iconClassName={isLoading ? 'animate-spin' : undefined}
      iconSize={20}
      data-test={primaryCtaDataTest[primaryCta]}
    />
  );
};

const renderSecondaryCtaButton = ({
  microcopy,
  onManageSeats,
}: Pick<ProductPricingProps, 'microcopy'> &
  Pick<ProductPricingPropsActions, 'onManageSeats'>) => {
  return (
    <ButtonV2
      label={microcopy.manageSeats}
      variant="secondary-blue-monochrome"
      size="large"
      shape="square"
      className="w-full gap-x-2"
      iconRight="UserPlus"
      iconWeight="bold"
      iconSize={20}
      onClick={() => onManageSeats?.()}
      data-test="product-info-manage-seats-button"
    />
  );
};

const renderFavoriteButton = ({
  isFavorite,
  microcopy,
  onFavorite,
}: Pick<ProductPricingProps, 'isFavorite' | 'microcopy' | 'onFavorite'>) => {
  if (!onFavorite) {
    return null;
  }

  const isLoading = isFavorite == null;

  return (
    <ButtonV2
      variant={isFavorite ? 'primary' : 'secondary-blue-monochrome'}
      size="large"
      shape="square"
      iconLeft={isLoading ? 'SpinnerGap' : 'BookmarkSimple'}
      iconWeight={isFavorite ? 'fill' : 'bold'}
      iconClassName={isLoading ? 'animate-spin' : undefined}
      iconSize={20}
      aria-label={
        isLoading
          ? microcopy.favoritesLoading
          : isFavorite
            ? microcopy.removeFromFavorites
            : microcopy.addToFavorites
      }
      aria-pressed={isLoading ? undefined : Boolean(isFavorite)}
      disabled={isLoading}
      className="shrink-0"
      data-test="product-info-favorite-button"
      onClick={() => onFavorite()}
    />
  );
};

const helpTextClassName = 'text-xs text-grey-700 [&_p]:mb-0';

const renderHelpText = ({
  helpText,
  customEula,
  microcopy,
}: {
  helpText: ProductPricingHelpText;
} & Pick<ProductPricingProps, 'customEula' | 'microcopy'>) => {
  let html: string | undefined;
  switch (helpText) {
    case 'eula':
      html = customEula
        ? interpolateStringWithData(microcopy.customEulaMessage, {
            custom: customEula,
          })
        : microcopy.standardEulaMessage;
      break;
    case 'refund-policy':
      html = microcopy.refundPolicy;
      break;
    case 'org-owns-unassigned-seats':
      html = microcopy.orgOwnsUnassignedSeats;
      break;
    case 'none':
      return null;
  }

  if (!html) {
    return null;
  }

  return (
    <RichText
      html={html}
      className={helpTextClassName}
      linkComponent={({ href, children, target, rel }) => (
        <LinkV2
          href={href}
          openInANewTab={target === '_blank'}
          variant="link-in-text-primary"
          rel={rel}
        >
          {children}
        </LinkV2>
      )}
    />
  );
};

const renderNotice = (
  notice: ProductPricingNotice,
  microcopy: ProductPricingProps['microcopy']
) => {
  if (notice === 'none') {
    return null;
  }

  const text =
    notice === 'another-org-owns'
      ? microcopy.anotherOrgOwnsAsset
      : microcopy.alreadyOwnInAnotherOrg;

  return (
    <p className="text-xs leading-5 tracking-[-0.024px] text-grey-700">
      {text}
    </p>
  );
};

const renderSecondaryHelpText = ({
  unassignedSeatCount,
  microcopy,
}: Pick<ProductPricingProps, 'unassignedSeatCount' | 'microcopy'>) => (
  <p className="text-xs leading-5 tracking-[-0.024px] text-grey-700">
    {interpolateStringWithData(microcopy.orgOwnsUnassignedSeatsWithCount, {
      count: unassignedSeatCount ?? 0,
    })}
  </p>
);

export const ProductPricing = ({
  variant = 'default',
  entitlement,
  price,
  sale,
  customEula,
  userType,
  isFavorite,
  isPurchasing = false,
  isClaiming = false,
  isManageSeatEnabled,
  isSeatAssigned,
  unassignedSeatCount,
  hasSeatData,
  microcopy,
  className = '',
  onAddToMyAssets,
  onBuyNow,
  onRequestSeat,
  onOpenInUnity,
  onFavorite,
  onManageSeats,
}: ProductPricingProps) => {
  const [quantity, setQuantity] = useState(1);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (variant !== 'default' || !sale?.dates?.ends) return;
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [variant, sale?.dates?.ends]);

  const isPreviewMode = entitlement === 'preview';

  const view = resolveProductPricingView({
    entitlement,
    price,
    userType,
    isSeatAssigned,
    isManageSeatEnabled,
    hasSeatData,
  });

  const ctaLabel = primaryCtaLabel(view.primaryCta, microcopy);
  const isCtaLoading = isPurchasing || isClaiming;

  const handlePrimaryCta = () => {
    switch (view.primaryCta) {
      case 'add-to-assets':
        return onAddToMyAssets?.();
      case 'buy-now':
        return onBuyNow?.(quantity);
      case 'request-seat':
        return onRequestSeat?.(quantity);
      case 'open-in-unity':
        return onOpenInUnity?.();
    }
  };

  const saleCountdownFormatted =
    variant === 'default' && sale?.dates?.ends && sale.dates.ends > now
      ? formatCountdown(
          sale.dates.ends,
          intervalToDuration({ start: now, end: sale.dates.ends }),
          {
            withDays: microcopy.sale.countdownWithDays,
            hoursOnly: microcopy.sale.countdownHoursOnly,
          }
        )
      : null;

  return variant === 'default' ? (
    <div className={c('flex flex-col gap-y-4', className)}>
      <div className="space-y-1">
        <div className="flex gap-2">
          <Price
            className={c(
              'text-grey-900',
              isFree(price)
                ? 'text-md font-normal leading-7 tracking-[-0.108px]'
                : 'text-xl font-medium leading-8 tracking-[-0.72px]'
            )}
            value={isFree(price) ? undefined : String(price.currentPrice)}
            currency={price.currency}
            microcopy={{ free: microcopy.free }}
          />
          {price.currentPrice !== price.originalPrice && (
            <Price
              className="text-xl font-medium leading-8 tracking-[-0.72px] text-grey-400 line-through"
              value={String(price.originalPrice)}
              currency={price.currency}
              microcopy={{ free: microcopy.free }}
            />
          )}
        </div>
        {(price.discountPercent || sale) && (
          <div className="flex gap-2">
            {sale?.type === 'new_release_discount' && (
              <Badge variant="default" text={microcopy.sale.newRelease} />
            )}
            {Boolean(price.discountPercent) && (
              <Badge variant="sale" text={`-${price.discountPercent}%`} />
            )}

            {saleCountdownFormatted && (
              <p
                suppressHydrationWarning
                className="text-xs leading-5 tracking-[-0.024px] text-grey-700"
              >
                {microcopy.sale.endsIn} {saleCountdownFormatted}
              </p>
            )}
          </div>
        )}
      </div>

      {view.showQuantitySelector ? (
        <div className="space-y-1">
          <div className="text-sm leading-5 tracking-[-0.028px] text-grey-700">
            {microcopy.seats}
          </div>
          <QuantitySelector
            value={quantity}
            onDecreaseQuantity={() => setQuantity((q) => q - 1)}
            onIncreaseQuantity={() => setQuantity((q) => q + 1)}
            microcopy={microcopy.quantitySelector}
          />
        </div>
      ) : (
        <></>
      )}

      {isPreviewMode ? (
        <div>{renderFavoriteButton({ isFavorite, microcopy, onFavorite })}</div>
      ) : (
        <div>
          <div className="space-y-2">
            {renderNotice(view.notice, microcopy)}
            <div className="flex gap-2">
              {renderCtaButton({
                label: ctaLabel,
                primaryCta: view.primaryCta,
                isLoading: isCtaLoading,
                iconRight:
                  view.primaryCta === 'request-seat' ? 'UserPlus' : undefined,
                onClick: handlePrimaryCta,
              })}
              {renderFavoriteButton({ isFavorite, microcopy, onFavorite })}
            </div>
            {renderHelpText({ helpText: view.helpText, customEula, microcopy })}
          </div>
          {view.showSecondaryCta && (
            <div
              className={c(
                'space-y-1',
                view.helpText === 'none' ? 'mt-2' : 'mt-6'
              )}
            >
              {renderSecondaryCtaButton({ microcopy, onManageSeats })}
              {view.showSecondaryHelpText &&
                renderSecondaryHelpText({ unassignedSeatCount, microcopy })}
            </div>
          )}
        </div>
      )}
    </div>
  ) : (
    <div
      className={c(
        'grid w-full grid-cols-[auto_auto] items-center justify-between gap-x-4 gap-y-2 lg:max-w-[496px] lg:grid-cols-[auto_1fr] lg:gap-x-8',
        className
      )}
    >
      <div className="space-y-1">
        <div className="flex gap-2">
          <Price
            className="text-base font-normal leading-[22px] text-grey-900 lg:text-md lg:leading-[26px]"
            value={isFree(price) ? undefined : String(price.currentPrice)}
            currency={price.currency}
            microcopy={{ free: microcopy.free }}
          />
          {price.currentPrice !== price.originalPrice && (
            <Price
              className="text-base font-normal leading-[22px] text-grey-400 line-through lg:text-md lg:leading-[26px]"
              value={String(price.originalPrice)}
              currency={price.currency}
              microcopy={{ free: microcopy.free }}
            />
          )}
        </div>
        {(price.discountPercent || sale) && (
          <div className="flex gap-2">
            {sale?.type === 'new_release_discount' && (
              <Badge variant="default" text={microcopy.sale.newRelease} />
            )}
            {Boolean(price.discountPercent) && (
              <Badge variant="sale" text={`-${price.discountPercent}%`} />
            )}
          </div>
        )}
      </div>

      {isPreviewMode ? (
        <div className="flex gap-2">
          {renderFavoriteButton({ isFavorite, microcopy, onFavorite })}
        </div>
      ) : (
        <div className="flex gap-2">
          {renderCtaButton({
            label: ctaLabel,
            primaryCta: view.primaryCta,
            isLoading: isCtaLoading,
            iconRight:
              view.primaryCta === 'request-seat' ? 'UserPlus' : undefined,
            onClick: handlePrimaryCta,
          })}
          {renderFavoriteButton({
            isFavorite,
            microcopy,
            onFavorite,
          })}
        </div>
      )}

      {!isPreviewMode &&
        (view.notice !== 'none' || view.helpText !== 'none') && (
          <div className="col-span-2 space-y-2 lg:col-span-1 lg:col-start-2">
            {renderNotice(view.notice, microcopy)}
            {renderHelpText({ helpText: view.helpText, customEula, microcopy })}
          </div>
        )}
    </div>
  );
};
